import { Controller } from '@hotwired/stimulus';
import Quill from 'quill';
import QuillMarkdown from 'quilljs-markdown';
import mention from 'quill-mention/autoregister';
import QuillAutoLink from '../lib/quill_auto_link';

export default class extends Controller {

  static targets = ['container', 'input'];

  static values = {
    inline: Boolean,
    jwt: String,
    contextType: String,
    contextId: String,
    allowTagEveryone: {
      type: Boolean,
      default: true
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  }

  connect() {
    Quill.register('modules/QuillMarkdown', QuillMarkdown, true);
    Quill.register('modules/autoLink', QuillAutoLink, true);
    const jwt = this.jwtValue;
    const hasMentions = jwt !== null && jwt !== undefined && jwt !== '';
    const contextType = this.contextTypeValue;
    const contextId = this.contextIdValue;
    const allowTagEveryone = this.allowTagEveryoneValue;
    const quill = new Quill(this.containerTarget, {
      modules: {
        toolbar: false,
        keyboard: {
          bindings: {
            enter: {
              key: 'Enter',
              handler: (range, context) => {
                if (this.inlineValue) {
                  quill.getModule('autoLink').flush();
                  this.inputTarget.closest('form').requestSubmit();
                } else {
                  quill.insertText(range.index, '\n');
                }
              }
            },
          }
        },
        QuillMarkdown: {},
        autoLink: {},
        mention: {
          allowedChars: /^[A-Za-z0-9\sÅÄÖåäö]*$/,
          mentionDenotationChars: ["@"],
          source: function (searchTerm, renderList, mentionChar) {
            if (hasMentions) {
              const params = new URLSearchParams({
                jwt: jwt,
                query: searchTerm
              });

              if (contextType) {
                params.append("context_type", contextType);
              }
              if (contextId) {
                params.append("context_id", contextId);
              }
              if (!allowTagEveryone) {
                params.append("allow_tag_everyone", "false");
              }

              fetch(`/private_api/member_mentions?${params}`)
                .then(function (response) {
                  return response.json();
                })
                .then(function (data) {
                  renderList(data, searchTerm);
                })
                .catch(function (error) {
                  console.error("Error fetching mentioned users:", error);
                });
            }
          },
        }

      },
      readOnly: false,
      theme: 'snow',
      formats: this.buildFormats()
    });

    const input = this.inputTarget;

    if (input.value !== null && input.value !== undefined && input.value !== '') {
      quill.clipboard.dangerouslyPasteHTML(input.value);
    }

    quill.on('text-change', (delta, oldDelta, source) => {
      if (quill.getText().trim().length === 0) {
        input.value = '';
      } else {
        input.value = quill.getSemanticHTML()
          .replaceAll('<p></p>', '<p><br/></p>')
          .replaceAll('&nbsp;', ' ');
      }
    });

    this.quillObject = quill;

    if (this.autofocusValue) {
      setTimeout(() => {
        quill.focus();
        const length = quill.getLength();
        quill.setSelection(length, 0);
      }, 0);

      this.focusHandler = this.focus.bind(this);
      window.addEventListener('richEditor:focusInput', this.focusHandler);
    }
  }

  disconnect() {
    if (this.autofocusValue) {
      window.removeEventListener('richEditor:focusInput', this.focusHandler);
    }
  }

  focus() {
    const length = this.quillObject.getLength();
    this.quillObject.focus();
    this.quillObject.setSelection(length, 0);
  }

  buildFormats() {
    if (this.inlineValue) {
      return [
        'bold',
        'italic',
        'list',
        'link',
        'mention'
      ]
    } else {
      return [
        'bold',
        'italic',
        'list',
        'link',
        'header',
        'mention'
      ]
    }
  }

  handleChange(event) {
    this.quillObject.root.innerHTML = event.detail.content;
  }

}